import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { getAuthToken } from "lib/utils/auth";
import { isHttpSecure } from "lib/utils/utils";
import type { RootState } from "store";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";

const defaultHost = process.env.REACT_APP_SINGLE_ARC_HOSTNAME || window.location.hostname;
const scheme = isHttpSecure() ? "https" : "http";

export const formatBehaviourApiError = (
    error: unknown,
    fallback = "Request failed.",
): string => {
    const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                if (typeof item === "string") return item;
                if (item && typeof item === "object" && "msg" in item) {
                    return String((item as { msg?: string }).msg);
                }
                return JSON.stringify(item);
            })
            .join("; ");
    }
    if (typeof detail === "object" && detail !== null && "msg" in detail) {
        return String((detail as { msg?: string }).msg);
    }
    return fallback;
};

const makeBehaviourQuery = (host: string, arcId?: string | null) =>
    fetchBaseQuery({
        // Direct ARC mode uses arc_gateway; fleet mode uses the proxy path.
        baseUrl: gatewayServiceBaseUrl("behaviour", arcId, host) ?? `${scheme}://${host}:12407/`,
        prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) headers.set("Authorization", `Bearer ${token}`);
            return headers;
        },
    });

const _defaultQuery = makeBehaviourQuery(defaultHost);

const behaviourBaseQuery: BaseQueryFn = (args, api, extraOptions) => {
    const { arcId, arcProxyHost } = (api.getState() as RootState).arcProxy;
    const effectiveHost = arcId && arcProxyHost ? arcProxyHost : defaultHost;
    if (arcId && !arcProxyHost) {
        return { error: { status: "CUSTOM_ERROR", error: "Fleet proxy host is unavailable" } };
    }
    const innerQuery = !arcId && effectiveHost === defaultHost
        ? _defaultQuery
        : makeBehaviourQuery(effectiveHost, arcId);
    return innerQuery(args, api, extraOptions);
};

export interface BehaviourCondition {
    source: string;
    topic: string;
    field: string;
    operator: string;
    value: string;
}

export interface BehaviourRule {
    id: string;
    name: string;
    enabled: boolean;
    conditions: {
        logic: string;
        items: BehaviourCondition[];
    };
    action: {
        type: string;
        params: Record<string, string>;
    };
}

export interface BehaviourFailurePolicy {
    on_failure:
        | "stop"
        | "continue"
        | "rtl"
        | "retry"
        | "plan_next"
        | "relocalize_prior";
    rtl_mode?: string;
    retry_count?: string;
    on_retry_exhausted?: "stop" | "rtl" | "continue";
    plan_next_max?: string;
    on_plan_next_exhausted?: "stop" | "rtl" | "continue";
    skip_follow_on_actions?: string;
    enabled?: boolean;
}

export interface BehaviourMissionLightEntry {
    enabled?: boolean;
}

export interface BehaviourMissionAudio {
    track_id?: string;
    interval_sec?: number | string;
    volume?: number | string;
    name?: string;
    enabled?: boolean;
}

export interface BehaviourProfile {
    id: string;
    name: string;
    rules: BehaviourRule[];
    failure_policies?: Record<string, BehaviourFailurePolicy>;
    mission_lights?: Record<string, BehaviourMissionLightEntry | boolean | string>;
    mission_audio?: BehaviourMissionAudio;
}

export interface BehaviourProfileSummary {
    id: string;
    name: string;
    rule_count: number;
}

export interface BehaviourMissionAssignment {
    mission_id: string;
    profile_id: string;
}

export interface BehaviourActionFeedback {
    type: string;
    status: "running" | "completed" | "failed";
    message: string;
    mission_id?: string;
    rule_id?: string;
    updated_at?: number;
}

export interface BehaviourRuntimeStatus {
    mission_active: boolean;
    active_mission_id?: string | null;
    active_profile_id?: string | null;
    active_action?: BehaviourActionFeedback | null;
    last_action?: BehaviourActionFeedback | null;
}

export interface BehaviourCatalogActionParamOption {
    value: string;
    label: string;
}

export interface BehaviourCatalogActionParam {
    name: string;
    label: string;
    options?: BehaviourCatalogActionParamOption[];
    when_param?: string;
    when_value?: string;
}

export interface BehaviourCatalogAction {
    type: string;
    label: string;
    params: BehaviourCatalogActionParam[];
    for_mission_step_failure?: boolean;
    for_mission_lights?: boolean;
    for_mission_audio?: boolean;
    light_type?: string;
    for_failure_action_types?: string[];
    exclude_source_ids?: string[];
}

export interface BehaviourCatalogEventSource {
    id: string;
    label: string;
    topic: string;
    field: string;
    value_type: string;
    operators: string[];
    kind?: string;
    action_type?: string;
    value_options?: BehaviourCatalogActionParamOption[];
}

export interface BehaviourCatalog {
    event_sources: BehaviourCatalogEventSource[];
    actions: BehaviourCatalogAction[];
    condition_logic: string[];
    failure_policy_actions?: Array<{ action_type: string; label: string }>;
    failure_policy_options?: BehaviourCatalogActionParamOption[];
    failure_policy_rtl_modes?: BehaviourCatalogActionParamOption[];
    mission_light_types?: Array<{ light_type: string; label: string; action_type: string }>;
}

export const behaviourRulesApi = createApi({
    reducerPath: "behaviourRulesApi",
    baseQuery: behaviourBaseQuery,
    tagTypes: ["BehaviourProfile", "BehaviourMissionProfile", "BehaviourCatalog"],
    endpoints: (builder) => ({
        getBehaviourHealth: builder.query<{ status: string; service: string }, void>({
            query: () => "health",
        }),
        getBehaviourRuntime: builder.query<BehaviourRuntimeStatus, void>({
            query: () => "runtime",
        }),
        getBehaviourCatalog: builder.query<BehaviourCatalog, void>({
            query: () => "catalog",
            providesTags: [{ type: "BehaviourCatalog", id: "LIST" }],
        }),
        getBehaviourProfiles: builder.query<{ profiles: BehaviourProfileSummary[] }, void>({
            query: () => "profiles",
            providesTags: (result) =>
                result
                    ? [
                          ...result.profiles.map(({ id }) => ({
                              type: "BehaviourProfile" as const,
                              id,
                          })),
                          { type: "BehaviourProfile" as const, id: "LIST" },
                      ]
                    : [{ type: "BehaviourProfile" as const, id: "LIST" }],
        }),
        getBehaviourProfile: builder.query<{ profile: BehaviourProfile }, string>({
            query: (profileId) => `profiles/${encodeURIComponent(profileId)}`,
            providesTags: (_result, _error, profileId) => [{ type: "BehaviourProfile", id: profileId }],
        }),
        createBehaviourProfile: builder.mutation<
            { profile: BehaviourProfile },
            { name: string; id?: string }
        >({
            query: (body) => ({
                url: "profiles",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "BehaviourProfile", id: "LIST" }],
        }),
        updateBehaviourProfile: builder.mutation<
            { profile: BehaviourProfile },
            {
                profileId: string;
                name?: string;
                rules?: BehaviourRule[];
                failure_policies?: Record<string, BehaviourFailurePolicy>;
                mission_lights?: Record<string, BehaviourMissionLightEntry | boolean | string>;
                mission_audio?: BehaviourMissionAudio | Record<string, never>;
            }
        >({
            query: ({ profileId, ...body }) => ({
                url: `profiles/${encodeURIComponent(profileId)}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_result, _error, { profileId }) => [
                { type: "BehaviourProfile", id: profileId },
                { type: "BehaviourProfile", id: "LIST" },
            ],
        }),
        deleteBehaviourProfile: builder.mutation<{ deleted: boolean; id: string }, string>({
            query: (profileId) => ({
                url: `profiles/${encodeURIComponent(profileId)}`,
                method: "DELETE",
            }),
            invalidatesTags: () => [
                { type: "BehaviourProfile", id: "LIST" },
                { type: "BehaviourMissionProfile", id: "LIST" },
            ],
        }),
        getBehaviourMissionProfiles: builder.query<{ assignments: BehaviourMissionAssignment[] }, void>({
            query: () => "mission-profiles",
            providesTags: [{ type: "BehaviourMissionProfile", id: "LIST" }],
        }),
        getBehaviourMissionProfile: builder.query<{ assignment: BehaviourMissionAssignment | null }, string>({
            query: (missionId) => `mission-profiles/${encodeURIComponent(missionId)}`,
            providesTags: (_result, _error, missionId) => [{ type: "BehaviourMissionProfile", id: missionId }],
        }),
        setBehaviourMissionProfile: builder.mutation<
            { assignment: BehaviourMissionAssignment },
            { missionId: string; profileId: string }
        >({
            query: ({ missionId, profileId }) => ({
                url: `mission-profiles/${encodeURIComponent(missionId)}`,
                method: "PUT",
                body: { profile_id: profileId },
            }),
            invalidatesTags: (_result, _error, { missionId }) => [
                { type: "BehaviourMissionProfile", id: missionId },
                { type: "BehaviourMissionProfile", id: "LIST" },
            ],
        }),
    }),
});

export const {
    useGetBehaviourHealthQuery,
    useGetBehaviourRuntimeQuery,
    useGetBehaviourCatalogQuery,
    useGetBehaviourProfilesQuery,
    useGetBehaviourProfileQuery,
    useCreateBehaviourProfileMutation,
    useUpdateBehaviourProfileMutation,
    useDeleteBehaviourProfileMutation,
    useGetBehaviourMissionProfilesQuery,
    useGetBehaviourMissionProfileQuery,
    useSetBehaviourMissionProfileMutation,
} = behaviourRulesApi;
